import { z } from 'zod';

import { BookingGranularitySchema } from './distribution.js';

/**
 * Booking contracts (M6 — PM-2039/2040/2042). The daily ResourceBooking row
 * is canonical (DL-012): period-level input is distributed once and never
 * stored. Period aggregates come in M9.
 */

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected an ISO date (YYYY-MM-DD)');

/** A period entry covers at most one year of days (PM-2040). */
export const MAX_BOOKING_RANGE_DAYS = 366;

/**
 * Single-day entry (PM-2039): the hours are stored EXACTLY as given — no
 * rounding, no granularity. Zero is a valid value and keeps a row (an
 * explicit "nothing planned this day" is not the same as no row).
 */
export const UpsertBookingRequestSchema = z.object({
  hours: z.number().min(0).max(9999.99).multipleOf(0.01),
});
export type UpsertBookingRequest = z.infer<typeof UpsertBookingRequestSchema>;

/**
 * Period entry (PM-2040): exactly one of totalHours/percentage. The strategy
 * key resolves against the shared registry (PM-2043); granularity applies to
 * distribution only, never to single-day entry.
 */
export const PeriodBookingRequestSchema = z
  .object({
    startDate: isoDate,
    endDate: isoDate,
    totalHours: z.number().min(0).max(1_000_000).multipleOf(0.01).optional(),
    /** §5.3.1 — resolved against actual period capacity (DL-029 §3). May exceed 100. */
    percentage: z.number().gt(0).max(10_000).multipleOf(0.01).optional(),
    strategy: z.string().min(1).default('PROPORTIONAL'),
    granularity: BookingGranularitySchema.default(0.25),
  })
  .refine((body) => (body.totalHours === undefined) !== (body.percentage === undefined), {
    message: 'Provide exactly one of totalHours or percentage',
    path: ['totalHours'],
  });
export type PeriodBookingRequest = z.infer<typeof PeriodBookingRequestSchema>;

export const BookingListQuerySchema = z.object({
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export type BookingListQuery = z.infer<typeof BookingListQuerySchema>;

/** §5.6 range delete — both bounds required. */
export const DeleteBookingRangeQuerySchema = z.object({
  from: isoDate,
  to: isoDate,
});
export type DeleteBookingRangeQuery = z.infer<typeof DeleteBookingRangeQuerySchema>;

export const BookingDateParamsSchema = z.object({
  id: z.string().uuid(),
  date: isoDate,
});
export type BookingDateParams = z.infer<typeof BookingDateParamsSchema>;

export const BookingEntrySchema = z.object({
  date: isoDate,
  hours: z.number().min(0),
});
export type BookingEntry = z.infer<typeof BookingEntrySchema>;

export const BookingListResponseSchema = z.object({
  bookings: z.array(BookingEntrySchema),
});
export type BookingListResponse = z.infer<typeof BookingListResponseSchema>;

const DAY_MS = 86_400_000;

/**
 * Whole-day arithmetic on ISO dates via UTC epoch days — NEVER a local Date
 * (a DST-at-midnight zone breaks `+1 day`; see apps/web/src/lib/epochDay.ts).
 */
export const bookingRangeDayCount = (startDate: string, endDate: string): number =>
  Math.round((Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / DAY_MS) +
  1;

/** Every ISO date from startDate to endDate inclusive, in order. UTC-safe. */
export const enumerateBookingDays = (startDate: string, endDate: string): string[] => {
  const days: string[] = [];
  const end = Date.parse(`${endDate}T00:00:00Z`);
  for (let t = Date.parse(`${startDate}T00:00:00Z`); t <= end; t += DAY_MS) {
    days.push(new Date(t).toISOString().slice(0, 10));
  }
  return days;
};
